import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, StarIcon, TruckIcon, ShieldCheckIcon, XIcon } from 'lucide-react';
import { PACKAGES, Pkg } from '../data/content';
type Props = {
  compact?: boolean;
  mode?: 'inline' | 'modal';
  isOpen?: boolean;
  selectedId?: string;
  onSelect?: (packageId: string) => void;
  onClose?: () => void;
};
type PackageCardsProps = {
  selectedId: string;
  onSelect: (packageId: string) => void;
};
function PackageCards({
  selectedId,
  onSelect
}: PackageCardsProps) {
  return <div className="grid gap-3 sm:grid-cols-3" data-id="element-223" data-mp="3vxdkomgc2knw">
      {PACKAGES.map(pkg => {
      const active = selectedId === pkg.id;
      return <button key={pkg.id} onClick={() => onSelect(pkg.id)} className={`relative flex flex-col rounded-2xl border p-4 text-left transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-2 ${active ? 'border-forest bg-forest text-ivory shadow-lg' : 'border-forest/15 bg-ivory text-forest hover:border-forest/40'}`} data-id="element-224" data-mp="9jlv9hqga4yq">
            {pkg.popular && <span className={`absolute -top-2.5 left-4 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${active ? 'bg-ivory text-forest' : 'bg-forest text-ivory'}`} data-id="element-225" data-mp="3v3fji5xbb996">
                ⭐ Most Popular
              </span>}
            <span className={`text-xs font-medium ${active ? 'text-ivory/70' : 'text-sage'}`} data-id="element-226" data-mp="cwhkwii0dwt2">
              {pkg.sachets}
            </span>
            <span className="mt-1 font-serif text-lg font-medium leading-tight" data-id="element-227" data-mp="monchr4cmc7q">{pkg.name}</span>
            <span className="mt-3 flex items-baseline gap-1.5" data-id="element-228" data-mp="ro07iz5diple">
              <span className="text-2xl font-semibold" data-id="element-229" data-mp="rlnki7krdp1h">{pkg.price}</span>
              {pkg.original && <span className={`text-sm line-through ${active ? 'text-ivory/50' : 'text-forest/40'}`} data-id="element-230" data-mp="6yfkfew8woli">
                  {pkg.original}
                </span>}
            </span>
            {pkg.badge && <span className={`mt-2 inline-flex w-fit rounded-full px-2 py-0.5 text-[10px] font-semibold ${active ? 'bg-ivory/20 text-ivory' : 'bg-sage/20 text-forest'}`} data-id="element-231" data-mp="goqxbqxwq84j">
                {pkg.badge}
              </span>}
            <span className={`mt-3 flex h-5 w-5 items-center justify-center rounded-full border ${active ? 'border-ivory bg-ivory' : 'border-forest/30'}`} data-id="element-232" data-mp="kqlzsyrkqrjw">
              {active && <CheckIcon className="h-3.5 w-3.5 text-forest" data-id="element-233" data-mp="upbgio7qi5yk" />}
            </span>
          </button>;
    })}
    </div>;
}
function PackageMeta() {
  return <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-forest/60" data-id="element-234" data-mp="2wfwtqdj8709m">
      <span className="flex items-center gap-1.5" data-id="element-235" data-mp="1bx2rv209ahsu">
        <TruckIcon className="h-4 w-4 text-sage" data-id="element-236" data-mp="1bzowotvrij7d" /> Free Shipping
      </span>
      <span className="flex items-center gap-1.5" data-id="element-237" data-mp="1gvmcmz345njm">
        <ShieldCheckIcon className="h-4 w-4 text-sage" data-id="element-238" data-mp="1281ab7e2bscr" /> 30-Day Satisfaction Guarantee
      </span>
      <span className="flex items-center gap-1.5" data-id="element-239" data-mp="1762rtnvubjm4">
        <StarIcon className="h-4 w-4 fill-sage text-sage" data-id="element-240" data-mp="174rrxz5imw0a" /> 4.9/5 rating
      </span>
    </div>;
}
export function PackagePicker({
  compact = false,
  mode = 'inline',
  isOpen = false,
  selectedId,
  onSelect,
  onClose
}: Props) {
  const [internalSelected, setInternalSelected] = useState('wellness');
  const activeId = selectedId ?? internalSelected;
  const selectedPackage = PACKAGES.find(pkg => pkg.id === activeId) ?? PACKAGES[1];
  useEffect(() => {
    if (selectedId) {
      setInternalSelected(selectedId);
    }
  }, [selectedId]);
  const selectPackage = (packageId: string) => {
    setInternalSelected(packageId);
    onSelect?.(packageId);
  };
  if (mode === 'modal') {
    return <AnimatePresence data-id="element-241" data-mp="ihfgcc0nmv1d">
        {isOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[70] flex items-end justify-center bg-forest-deep/55 p-4 backdrop-blur-sm sm:items-center sm:p-6" role="dialog" aria-modal="true" aria-labelledby="package-dialog-title" onMouseDown={onClose} data-id="element-242" data-mp="dk6wm6zuyiy0">
            <motion.div initial={{
          opacity: 0,
          y: 24,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 24,
          scale: 0.98
        }} transition={{
          duration: 0.25,
          ease: [0.22, 1, 0.36, 1]
        }} className="w-full max-w-3xl rounded-[1.75rem] bg-ivory p-5 shadow-2xl sm:p-7" onMouseDown={event => event.stopPropagation()} data-id="element-243" data-mp="3qyjqvyympzwc">
              <div className="flex items-start justify-between gap-5" data-id="element-244" data-mp="382tmy674ytft">
                <div data-id="element-245" data-mp="1nhwvucuu8lvd">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sage" data-id="element-246" data-mp="1nkj7ankunliv">Choose your package</p>
                  <h2 id="package-dialog-title" className="mt-2 font-serif text-3xl text-forest" data-id="element-247" data-mp="1xgmfob6aoge9">
                    Find the ritual that fits your day.
                  </h2>
                </div>
                <button onClick={onClose} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-forest/15 text-forest transition-colors hover:bg-forest hover:text-ivory" aria-label="Close package selector" data-id="element-248" data-mp="14rxds9xf071g">
                  <XIcon className="h-5 w-5" data-id="element-249" data-mp="1ijvepxgz2wmj" />
                </button>
              </div>

              <div className="mt-8" data-id="element-250" data-mp="m1pczyh6tsof">
                <PackageCards selectedId={activeId} onSelect={selectPackage} data-id="element-251" data-mp="m0o1jsw9f466" />
              </div>
              <button onClick={onClose} className="mt-5 flex w-full items-center justify-center rounded-full bg-forest px-7 py-4 text-sm font-semibold text-ivory transition-colors hover:bg-forest-deep" data-id="element-252" data-mp="q1q3ihhk9ubu">
                Continue with {selectedPackage.name}
              </button>
              <PackageMeta data-id="element-253" data-mp="232yhy0z8i1df" />
            </motion.div>
          </motion.div>}
      </AnimatePresence>;
  }
  return <div className="w-full" data-id="element-254" data-mp="1af2r3cwcj3ok">
      <PackageCards selectedId={activeId} onSelect={selectPackage} data-id="element-255" data-mp="1aeavi22r15ya" />
      <a href="#packages" className="mt-4 flex w-full items-center justify-center rounded-full bg-forest px-8 py-4 text-sm font-semibold uppercase tracking-wide text-ivory transition-colors hover:bg-forest-deep" data-id="element-256" data-mp="15fzlnrcgevg2">
        Buy Now
      </a>
      {!compact && <PackageMeta data-id="element-257" data-mp="1fg9xb7iubut4" />}
    </div>;
}
export function getPackage(packageId: string): Pkg {
  return PACKAGES.find(pkg => pkg.id === packageId) ?? PACKAGES[1];
}